import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Auth() {
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1);

  const [loginData, setLoginData] = useState({ email: '', password: '', remember: false });
  const [registerData, setRegisterData] = useState({
    name: '', email: '', password: '', confirm: '', role: 'jobseeker', agree: false,
    company: '', website: '', phone: '', designation: '',
    location: '', experience: '', skills: '', bio: '',
  });
  const [showLoginPass, setShowLoginPass]     = useState(false);
  const [showRegPass, setShowRegPass]         = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const { loginUser } = useAuth();
  const navigate = useNavigate();

  // ── Validation ──
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateLogin = () => {
    if (!loginData.email)              return 'Email is required';
    if (!validateEmail(loginData.email)) return 'Enter a valid email address';
    if (!loginData.password)           return 'Password is required';
    if (loginData.password.length < 6) return 'Password must be at least 6 characters';
    return null;
  };

  const validateRegister = () => {
    if (!registerData.name.trim())       return 'Full name is required';
    if (registerData.name.length < 2)    return 'Name must be at least 2 characters';
    if (!validateEmail(registerData.email)) return 'Enter a valid email address';
    if (registerData.password.length < 6)   return 'Password must be at least 6 characters';
    if (registerData.password !== registerData.confirm) return 'Passwords do not match';
    if (registerData.role === 'employer') {
      if (!registerData.company.trim())     return 'Company name is required';
      if (!registerData.phone.trim())       return 'Phone number is required';
      if (!registerData.designation.trim()) return 'Your designation is required';
    }
    if (registerData.role === 'jobseeker') {
      if (!registerData.phone.trim())    return 'Phone number is required';
      if (!registerData.location.trim()) return 'Location is required';
    }
    if (!registerData.agree)             return 'Please accept the Terms of Service';
    return null;
  };

  // ── API call ──
  const apiCall = async (endpoint, body) => {
    const res = await fetch(`http://localhost:5001/api/auth/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Something went wrong');
    return data;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const err = validateLogin();
    if (err) return setError(err);
    setError(''); setLoading(true);
    try {
      const data = await apiCall('login', { email: loginData.email, password: loginData.password });
      loginUser(data.token, data.user);
      setSuccess(`Welcome back, ${data.user.name}! Redirecting...`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Cannot connect to server. Make sure the backend is running on port 5000.'
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const err = validateRegister();
    if (err) return setError(err);
    setError(''); setLoading(true);
    try {
      const data = await apiCall('register', {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
        role: registerData.role,
        ...(registerData.role === 'employer' && {
          company:     registerData.company,
          website:     registerData.website,
          phone:       registerData.phone,
          designation: registerData.designation,
        }),
        ...(registerData.role === 'jobseeker' && {
          phone:      registerData.phone,
          location:   registerData.location,
          experience: registerData.experience,
          skills:     registerData.skills.split(',').map(s => s.trim()).filter(Boolean),
          bio:        registerData.bio,
        }),
      });
      loginUser(data.token, data.user);
      setSuccess(`Account created! Welcome, ${data.user.name}! Redirecting...`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Cannot connect to server. Make sure the backend is running on port 5000.'
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (t) => { setTab(t); setError(''); setSuccess(''); setStep(1); };

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!registerData.name.trim())          return setError('Full name is required');
    if (!validateEmail(registerData.email)) return setError('Enter a valid email address');
    if (!registerData.phone.trim())         return setError('Phone number is required');
    if (!registerData.location.trim())      return setError('Location is required');
    setError('');
    setStep(2);
  };

  return (
    <div className="auth-page">
      {/* Left Panel */}
      <div className="auth-left">
        <div className="auth-left-content">
          <Link to="/" className="auth-logo">
            <div className="logo-icon"><img
                src="/justhiringlogo.png"
                alt="JustHiring24"
                style={{
                  height: 60,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: "50%",
                }}/></div>
            <div className="logo-text">
            <span className="logo-main">Just</span>
            <span className="logo-accent">Hiring</span>
            <span className="logo-accent">24</span>
            </div>
          </Link>
          <h2 className="auth-tagline">Your Dream Job<br />Is One Step Away</h2>
          <p className="auth-sub">Join thousands of professionals finding their perfect career match every day.</p>
          <div className="auth-stats">
            <div className="auth-stat"><span className="stat-num">50K+</span><span className="stat-label">Jobs Posted</span></div>
            <div className="auth-stat-divider"></div>
            <div className="auth-stat"><span className="stat-num">120K+</span><span className="stat-label">Job Seekers</span></div>
            <div className="auth-stat-divider"></div>
            <div className="auth-stat"><span className="stat-num">8K+</span><span className="stat-label">Companies</span></div>
          </div>
          <div className="auth-features">
            {[
              { icon: 'fa-check-circle', text: 'Free to create your profile' },
              { icon: 'fa-check-circle', text: 'Apply to jobs with one click' },
              { icon: 'fa-check-circle', text: 'Get notified for new matches' },
              { icon: 'fa-check-circle', text: 'Connect with top companies' },
            ].map((f, i) => (
              <div className="auth-feature-item" key={i}>
                <i className={`fa ${f.icon}`}></i>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="auth-right">
        <div className="auth-form-wrap">
          <div className="auth-tabs">
            <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => switchTab('login')}>Sign In</button>
            <button className={`auth-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => switchTab('register')}>Create Account</button>
          </div>

          {error   && <div className="auth-alert error"><i className="fa fa-exclamation-circle me-2"></i>{error}</div>}
          {success && <div className="auth-alert success"><i className="fa fa-check-circle me-2"></i>{success}</div>}

          {/* ── Login ── */}
          {tab === 'login' && (
            <div className="auth-form-body">
              <h3 className="form-title">Welcome back!</h3>
              <p className="form-subtitle">Sign in to continue your job search</p>


              <form onSubmit={handleLogin} noValidate>
                <div className="form-group-auth">
                  <label>Email Address</label>
                  <div className={`input-wrap ${error && !loginData.email ? 'input-error' : ''}`}>
                    <i className="fa fa-envelope input-icon"></i>
                    <input type="email" placeholder="you@example.com"
                      value={loginData.email}
                      onChange={e => setLoginData({ ...loginData, email: e.target.value })} />
                  </div>
                </div>

                <div className="form-group-auth">
                  <div className="d-flex justify-content-between">
                    <label>Password</label>
                    <a href="#" className="forgot-link">Forgot password?</a>
                  </div>
                  <div className="input-wrap">
                    <i className="fa fa-lock input-icon"></i>
                    <input type={showLoginPass ? 'text' : 'password'} placeholder="Enter your password"
                      value={loginData.password}
                      onChange={e => setLoginData({ ...loginData, password: e.target.value })} />
                    <button type="button" className="toggle-pass" onClick={() => setShowLoginPass(!showLoginPass)}>
                      <i className={`fa ${showLoginPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="d-flex align-items-center mb-4 gap-2">
                  <input type="checkbox" id="remember" className="auth-checkbox"
                    checked={loginData.remember}
                    onChange={e => setLoginData({ ...loginData, remember: e.target.checked })} />
                  <label htmlFor="remember" className="checkbox-label">Remember me for 30 days</label>
                </div>

                <button type="submit" className="btn-auth-submit" disabled={loading}>
                  {loading
                    ? <><i className="fa fa-spinner fa-spin me-2"></i>Signing in...</>
                    : <>Sign In <i className="fa fa-arrow-right ms-2"></i></>}
                </button>
              </form>

              <p className="auth-switch">
                Don't have an account?{' '}
                <button className="switch-link" onClick={() => switchTab('register')}>Create one free</button>
              </p>
            </div>
          )}

          {/* ── Register ── */}
          {tab === 'register' && (
            <div className="auth-form-body">
              <h3 className="form-title">Create your account</h3>
              <p className="form-subtitle">Start your journey to the perfect job</p>

              <div className="role-selector">
                <button type="button"
                  className={`role-btn ${registerData.role === 'jobseeker' ? 'active' : ''}`}
                  onClick={() => setRegisterData({ ...registerData, role: 'jobseeker' })}>
                  <i className="fa fa-user-tie"></i>
                  <span>Job Seeker</span>
                  <small>Find your dream job</small>
                </button>
                <button type="button"
                  className={`role-btn ${registerData.role === 'employer' ? 'active' : ''}`}
                  onClick={() => setRegisterData({ ...registerData, role: 'employer' })}>
                  <i className="fa fa-building"></i>
                  <span>Employer</span>
                  <small>Hire top talent</small>
                </button>
              </div>

              {/* ── Jobseeker Multi-Step ── */}
              {registerData.role === 'jobseeker' ? (
                <>
                  {/* Step Indicator */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                    {['Basic Info', 'Professional', 'Security'].map((s, i) => (
                      <React.Fragment key={i}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: step > i + 1 ? '#00C896' : step === i + 1 ? '#0066FF' : '#eef0f8', color: step >= i + 1 ? '#fff' : '#aaa', transition: 'all .3s' }}>
                            {step > i + 1 ? <i className="fa fa-check" style={{ fontSize: 10 }}></i> : i + 1}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: step === i + 1 ? '#0066FF' : step > i + 1 ? '#00C896' : '#aaa' }}>{s}</span>
                        </div>
                        {i < 2 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? '#00C896' : '#eef0f8', borderRadius: 2, transition: 'all .3s' }}></div>}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Step 1 — Basic Info */}
                  {step === 1 && (
                    <form onSubmit={handleStep1} noValidate>
                      <div className="form-row-2">
                        <div className="form-group-auth">
                          <label>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                          <div className="input-wrap">
                            <i className="fa fa-user input-icon"></i>
                            <input type="text" placeholder="John Doe"
                              value={registerData.name}
                              onChange={e => setRegisterData({ ...registerData, name: e.target.value })} />
                          </div>
                        </div>
                        <div className="form-group-auth">
                          <label>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                          <div className="input-wrap">
                            <i className="fa fa-phone input-icon"></i>
                            <input type="tel" placeholder="+91 00000 00000"
                              value={registerData.phone}
                              onChange={e => setRegisterData({ ...registerData, phone: e.target.value })} />
                          </div>
                        </div>
                      </div>
                      <div className="form-group-auth">
                        <label>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                        <div className="input-wrap">
                          <i className="fa fa-envelope input-icon"></i>
                          <input type="email" placeholder="you@example.com"
                            value={registerData.email}
                            onChange={e => setRegisterData({ ...registerData, email: e.target.value })} />
                        </div>
                      </div>
                      <div className="form-group-auth">
                        <label>Location <span style={{ color: '#ef4444' }}>*</span></label>
                        <div className="input-wrap">
                          <i className="fa fa-map-marker-alt input-icon"></i>
                          <input type="text" placeholder="e.g. Mumbai, India"
                            value={registerData.location}
                            onChange={e => setRegisterData({ ...registerData, location: e.target.value })} />
                        </div>
                      </div>
                      <button type="submit" className="btn-auth-submit">
                        Next Step <i className="fa fa-arrow-right ms-2"></i>
                      </button>
                    </form>
                  )}

                  {/* Step 2 — Professional Info */}
                  {step === 2 && (
                    <form onSubmit={e => { e.preventDefault(); setError(''); setStep(3); }} noValidate>
                      <div className="form-row-2">
                        <div className="form-group-auth">
                          <label>Experience Level</label>
                          <div className="input-wrap">
                            <i className="fa fa-briefcase input-icon"></i>
                            <select value={registerData.experience} onChange={e => setRegisterData({ ...registerData, experience: e.target.value })}>
                              <option value="">Select experience</option>
                              <option>Fresher (0-1 years)</option>
                              <option>Junior (1-3 years)</option>
                              <option>Mid (3-5 years)</option>
                              <option>Senior (5+ years)</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group-auth">
                          <label>Job Category</label>
                          <div className="input-wrap">
                            <i className="fa fa-th-large input-icon"></i>
                            <select value={registerData.category || ''} onChange={e => setRegisterData({ ...registerData, category: e.target.value })}>
                              <option value="">Select category</option>
                              {['Technology','Marketing','Design','HR','Finance','Education','Healthcare','Sales'].map(c => <option key={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="form-group-auth">
                        <label>Skills <span style={{ color: '#aaa', fontWeight: 400, fontSize: 12 }}>(comma separated)</span></label>
                        <div className="input-wrap">
                          <i className="fa fa-tools input-icon"></i>
                          <input type="text" placeholder="React, Node.js, Python, Figma..."
                            value={registerData.skills}
                            onChange={e => setRegisterData({ ...registerData, skills: e.target.value })} />
                        </div>
                        {registerData.skills && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {registerData.skills.split(',').filter(s => s.trim()).map((s, i) => (
                              <span key={i} style={{ background: '#e8f0ff', color: '#0066FF', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 50 }}>{s.trim()}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="form-group-auth">
                        <label>Bio <span style={{ color: '#aaa', fontWeight: 400, fontSize: 12 }}>(optional)</span></label>
                        <div className="input-wrap textarea">
                          <i className="fa fa-comment-alt input-icon"></i>
                          <textarea rows={3} placeholder="Tell employers about yourself, your goals and strengths..."
                            value={registerData.bio}
                            onChange={e => setRegisterData({ ...registerData, bio: e.target.value })} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', border: '1.5px solid #eef0f8', borderRadius: 10, background: '#fafbff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                          <i className="fa fa-arrow-left me-2"></i> Back
                        </button>
                        <button type="submit" className="btn-auth-submit" style={{ flex: 2 }}>
                          Next Step <i className="fa fa-arrow-right ms-2"></i>
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Step 3 — Security */}
                  {step === 3 && (
                    <form onSubmit={handleRegister} noValidate>
                      <div className="form-group-auth">
                        <label>Password <span style={{ color: '#ef4444' }}>*</span></label>
                        <div className="input-wrap">
                          <i className="fa fa-lock input-icon"></i>
                          <input type={showRegPass ? 'text' : 'password'} placeholder="Min. 6 characters"
                            value={registerData.password}
                            onChange={e => setRegisterData({ ...registerData, password: e.target.value })} />
                          <button type="button" className="toggle-pass" onClick={() => setShowRegPass(!showRegPass)}>
                            <i className={`fa ${showRegPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                      {registerData.password && (
                        <div className="password-strength">
                          <div className="strength-bars">
                            {[1,2,3,4].map(n => (
                              <div key={n} className={`strength-bar ${registerData.password.length >= n * 2 ? getStrengthClass(registerData.password) : ''}`}></div>
                            ))}
                          </div>
                          <span className={`strength-text ${getStrengthClass(registerData.password)}`}>{getStrengthLabel(registerData.password)}</span>
                        </div>
                      )}
                      <div className="form-group-auth">
                        <label>Confirm Password <span style={{ color: '#ef4444' }}>*</span></label>
                        <div className="input-wrap">
                          <i className="fa fa-lock input-icon"></i>
                          <input type={showConfirmPass ? 'text' : 'password'} placeholder="Repeat password"
                            value={registerData.confirm}
                            onChange={e => setRegisterData({ ...registerData, confirm: e.target.value })} />
                          <button type="button" className="toggle-pass" onClick={() => setShowConfirmPass(!showConfirmPass)}>
                            <i className={`fa ${showConfirmPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                      <div className="d-flex align-items-start mb-4 gap-2">
                        <input type="checkbox" id="agree" className="auth-checkbox mt-1"
                          checked={registerData.agree}
                          onChange={e => setRegisterData({ ...registerData, agree: e.target.checked })} />
                        <label htmlFor="agree" className="checkbox-label">
                          I agree to the <Link to="/terms-of-service" className="auth-link">Terms of Service</Link> and <Link to="/privacy-policy" className="auth-link">Privacy Policy</Link>
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" onClick={() => setStep(2)} style={{ flex: 1, padding: '12px', border: '1.5px solid #eef0f8', borderRadius: 10, background: '#fafbff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                          <i className="fa fa-arrow-left me-2"></i> Back
                        </button>
                        <button type="submit" className="btn-auth-submit" style={{ flex: 2 }} disabled={loading}>
                          {loading ? <><i className="fa fa-spinner fa-spin me-2"></i>Creating...</> : <>Create Account <i className="fa fa-arrow-right ms-2"></i></>}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              ) : (
                /* ── Employer Single Form ── */
                <form onSubmit={handleRegister} noValidate>
                  <div className="form-group-auth">
                    <label>Contact Person Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <div className="input-wrap">
                      <i className="fa fa-user input-icon"></i>
                      <input type="text" placeholder="HR Manager / Owner Name"
                        value={registerData.name}
                        onChange={e => setRegisterData({ ...registerData, name: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group-auth">
                    <label>Company Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <div className="input-wrap">
                      <i className="fa fa-building input-icon"></i>
                      <input type="text" placeholder="e.g. TechCorp Pvt. Ltd."
                        value={registerData.company}
                        onChange={e => setRegisterData({ ...registerData, company: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row-2">
                    <div className="form-group-auth">
                      <label>Designation <span style={{ color: '#ef4444' }}>*</span></label>
                      <div className="input-wrap">
                        <i className="fa fa-id-badge input-icon"></i>
                        <input type="text" placeholder="e.g. HR Manager"
                          value={registerData.designation}
                          onChange={e => setRegisterData({ ...registerData, designation: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-group-auth">
                      <label>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                      <div className="input-wrap">
                        <i className="fa fa-phone input-icon"></i>
                        <input type="tel" placeholder="+91 00000 00000"
                          value={registerData.phone}
                          onChange={e => setRegisterData({ ...registerData, phone: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group-auth">
                    <label>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                    <div className="input-wrap">
                      <i className="fa fa-envelope input-icon"></i>
                      <input type="email" placeholder="you@company.com"
                        value={registerData.email}
                        onChange={e => setRegisterData({ ...registerData, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group-auth">
                    <label>Company Website <span style={{ color: '#aaa', fontWeight: 400, fontSize: 12 }}>(optional)</span></label>
                    <div className="input-wrap">
                      <i className="fa fa-globe input-icon"></i>
                      <input type="url" placeholder="https://yourcompany.com"
                        value={registerData.website}
                        onChange={e => setRegisterData({ ...registerData, website: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row-2">
                    <div className="form-group-auth">
                      <label>Password <span style={{ color: '#ef4444' }}>*</span></label>
                      <div className="input-wrap">
                        <i className="fa fa-lock input-icon"></i>
                        <input type={showRegPass ? 'text' : 'password'} placeholder="Min. 6 characters"
                          value={registerData.password}
                          onChange={e => setRegisterData({ ...registerData, password: e.target.value })} />
                        <button type="button" className="toggle-pass" onClick={() => setShowRegPass(!showRegPass)}>
                          <i className={`fa ${showRegPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                    </div>
                    <div className="form-group-auth">
                      <label>Confirm Password <span style={{ color: '#ef4444' }}>*</span></label>
                      <div className="input-wrap">
                        <i className="fa fa-lock input-icon"></i>
                        <input type={showConfirmPass ? 'text' : 'password'} placeholder="Repeat password"
                          value={registerData.confirm}
                          onChange={e => setRegisterData({ ...registerData, confirm: e.target.value })} />
                        <button type="button" className="toggle-pass" onClick={() => setShowConfirmPass(!showConfirmPass)}>
                          <i className={`fa ${showConfirmPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  {registerData.password && (
                    <div className="password-strength">
                      <div className="strength-bars">
                        {[1,2,3,4].map(n => (
                          <div key={n} className={`strength-bar ${registerData.password.length >= n * 2 ? getStrengthClass(registerData.password) : ''}`}></div>
                        ))}
                      </div>
                      <span className={`strength-text ${getStrengthClass(registerData.password)}`}>{getStrengthLabel(registerData.password)}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-start mb-4 gap-2">
                    <input type="checkbox" id="agree" className="auth-checkbox mt-1"
                      checked={registerData.agree}
                      onChange={e => setRegisterData({ ...registerData, agree: e.target.checked })} />
                    <label htmlFor="agree" className="checkbox-label">
                      I agree to the <Link to="/terms-of-service" className="auth-link">Terms of Service</Link> and <Link to="/privacy-policy" className="auth-link">Privacy Policy</Link>
                    </label>
                  </div>
                  <button type="submit" className="btn-auth-submit" disabled={loading}>
                    {loading ? <><i className="fa fa-spinner fa-spin me-2"></i>Creating account...</> : <>Create Account <i className="fa fa-arrow-right ms-2"></i></>}
                  </button>
                </form>
              )}

              <p className="auth-switch">
                Already have an account?{' '}
                <button className="switch-link" onClick={() => switchTab('login')}>Sign in</button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getStrengthClass(p) {
  if (p.length < 4) return 'weak';
  if (p.length < 8) return 'fair';
  if (/[A-Z]/.test(p) && /[0-9]/.test(p)) return 'strong';
  return 'good';
}
function getStrengthLabel(p) {
  return { weak: 'Weak', fair: 'Fair', good: 'Good', strong: 'Strong' }[getStrengthClass(p)];
}
